import { z } from 'zod';
import {
  E164,
  RideBatchAllocationSchema,
  RideBatchLinkAccessSchema,
  RideBatchRouteSchema,
  RideBatchWindowSchema,
  SaveRideBatchDraftInputSchema,
} from './batch_wire.js';

/** Celular digitado na tela, em qualquer formato: o BFF o normaliza para E.164. */
const TypedPhoneSchema = z.string().trim().min(8).max(24);

/** Uma pessoa como a tela manda: nome e celular do cadastro são do BFF. `phone` só vale quando o
 *  cadastro não tem um celular que a 99 aceite. */
const PersonRowSchema = z
  .object({
    userId: z.string().min(1),
    phone: TypedPhoneSchema.optional(),
    route: RideBatchRouteSchema,
  })
  .strict();

/** `POST /mobility/ride-batches` (web-chat → BFF): o `CreateRideBatchInput` sem o que o BFF
 *  decide (cliente, quem cria, nome do cliente, nome e celular de cada pessoa). `draftId` é o
 *  rascunho que esta liberação concretiza. */
export const CreateRideBatchBodySchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    window: RideBatchWindowSchema,
    allocation: RideBatchAllocationSchema,
    defaultRoute: RideBatchRouteSchema,
    people: z.array(PersonRowSchema).min(1).max(500),
    linkAccess: RideBatchLinkAccessSchema.default('app'),
    whatsapp: z.boolean().optional(),
    draftId: z.string().min(1).optional(),
  })
  .strict()
  .refine((b) => b.whatsapp !== false || b.linkAccess === 'app', {
    message: 'whatsapp_requires_app',
  });
export type CreateRideBatchBody = z.infer<typeof CreateRideBatchBodySchema>;

/** `POST /mobility/ride-batches/drafts` e `PUT .../drafts/:id`: o `SaveRideBatchDraftInput` sem o
 *  cliente e o autor (o BFF os põe) e com as pessoas como a tela as manda. */
export const SaveRideBatchDraftBodySchema = SaveRideBatchDraftInputSchema.omit({
  clientId: true,
  by: true,
  people: true,
})
  .extend({ people: z.array(PersonRowSchema).max(500) })
  .strict();
export type SaveRideBatchDraftBody = z.infer<typeof SaveRideBatchDraftBodySchema>;

/** `PATCH /mobility/ride-batches/:id`: nome, alocação, janela e/ou rota padrão. O autor é sempre o da sessão. */
export const UpdateRideBatchBodySchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    allocation: RideBatchAllocationSchema.optional(),
    window: RideBatchWindowSchema.optional(),
    defaultRoute: RideBatchRouteSchema.optional(),
  })
  .strict()
  .refine(
    (b) =>
      b.name !== undefined ||
      b.allocation !== undefined ||
      b.window !== undefined ||
      b.defaultRoute !== undefined,
    { message: 'empty_update' },
  );
export type UpdateRideBatchBody = z.infer<typeof UpdateRideBatchBodySchema>;

/** `POST /mobility/ride-batches/:id/links`: as pessoas novas do grupo liberado. */
export const AddRideBatchPeopleBodySchema = z
  .object({ people: z.array(PersonRowSchema).min(1).max(500) })
  .strict();
export type AddRideBatchPeopleBody = z.infer<typeof AddRideBatchPeopleBodySchema>;

/** `PATCH /mobility/ride-batches/:id/links/:linkId`. */
export const UpdateRideLinkBodySchema = z.object({ route: RideBatchRouteSchema }).strict();
export type UpdateRideLinkBody = z.infer<typeof UpdateRideLinkBodySchema>;

/** `POST /mobility/ride-batches/:id/links/:linkId/resend`: `phone` troca o número do link. */
export const ResendRideLinkBodySchema = z.object({ phone: TypedPhoneSchema.optional() }).strict();
export type ResendRideLinkBody = z.infer<typeof ResendRideLinkBodySchema>;

/** As recusas `400 { error, userIds }` do `POST /mobility/ride-batches` sobre as pessoas, na ordem
 *  em que o BFF as confere. `foreign_traveler` cobre também o id que não existe.
 *  `eligibility_unknown` (#7318): a regra da 99 não resolveu para a pessoa (leitura de permissão
 *  fora) — "não deu para verificar agora", nunca `not_eligible`; vem por último, depois das
 *  recusas que o gestor tem de corrigir. */
export const RideBatchPeopleErrorSchema = z.enum([
  'duplicate_traveler',
  'foreign_traveler',
  'not_eligible',
  'missing_phone',
  'eligibility_unknown',
]);
export type RideBatchPeopleError = z.infer<typeof RideBatchPeopleErrorSchema>;

/** Um viajante do cliente do gestor na busca (`GET /mobility/ride-batches/travelers?q=`). */
export const RideBatchTravelerSchema = z
  .object({
    userId: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    hasPhone: z.boolean(),
    /** O celular do cadastro em E.164, quando a 99 o aceita; `null` = sem celular utilizável. */
    phone: E164.nullable().optional(),
    /** `null` = a regra da 99 não resolveu agora (#7318): só sai para quem pede `mobilityState=1`. */
    eligible: z.boolean().nullable(),
  })
  .strict();
export const RideBatchTravelersResponseSchema = z
  .object({ travelers: z.array(RideBatchTravelerSchema) })
  .strict();
